/* eslint-disable max-len */
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Popover, Transition } from '@headlessui/react';
import { groupBy } from 'lodash';
import useGetHowToCreateBookingSteps from 'modules/account/calendar/utils/tour-steps/how-to-create-booking';
import useGetHowToCreateEventSteps from 'modules/account/calendar/utils/tour-steps/how-to-create-event';
import useGetHowToManageBookingSteps from 'modules/account/calendar/utils/tour-steps/how-to-manage-booking';
import useGetHowToManageEventSteps from 'modules/account/calendar/utils/tour-steps/how-to-manage-event';
import useGetHowToQuickCreateBookingSteps from 'modules/account/calendar/utils/tour-steps/how-to-quick-create-booking';
import useGetHowToQuickCreateEventSteps from 'modules/account/calendar/utils/tour-steps/how-to-quick-create-event';
import useGetHowToCreateDealSteps from 'modules/account/sales/utils/tour-steps/how-to-create-deal';
import useGetHowToCreateTaskSteps from 'modules/account/sales/utils/tour-steps/how-to-create-task';
import useGetHowToManageDealSteps from 'modules/account/sales/utils/tour-steps/how-to-manage-a-deal';
import useGetHowToManageTaskSteps from 'modules/account/sales/utils/tour-steps/how-to-manage-task';
import useGetHowToQuickCreateDealSteps from 'modules/account/sales/utils/tour-steps/how-to-quick-create-deal';
import useGetHowToQuickCreateTaskSteps from 'modules/account/sales/utils/tour-steps/how-to-quick-create-task';
import useGetViewCreatedDeals from 'modules/account/sales/utils/tour-steps/how-to-view-created-deals';
import useBeginTutorial from 'modules/account/calendar/utils/tour-steps/begin-tutorial';
import { MdClose } from 'react-icons/md';
import { Searchbox } from 'ui/components/form';
import { InfoIcon } from 'ui/components/icon';
import { IoMdHelpCircle } from 'react-icons/io';
import { useNavigate } from 'react-router-dom';
import { useProvider } from 'utils/providers/Provider';

import { cn } from '../../utils/helpers';
import { CalendarIcon, ProfileIcon, SalesIcon } from '../components/icon';
import useGetHowToCreateInvoice from './Tour/HowToCreateInvoice/useGetHowToCreateInvoice';
import useGetHowToManageInvoice from './Tour/HowToManageInvoice/useGetHowToManageInvoice';
import useGetHowToQuickCreateInvoice from './Tour/HowToQuickCreateInvoice/useGetHowToQuickCreateInvoice';
import {
  useGetHowToCreateCompanySteps,
  useGetHowToCreateLeadSteps,
  useGetHowToQuickCreateCompanySteps,
  useGetHowToQuickCreateLeadSteps,
  useGetViewCreatedCompanies,
  useGetViewCreatedLeads,
} from './utils/tour-steps';
import useGetHowToSetupPipelineSteps from './utils/tour-steps/how-to-setup-pipeline';
import useGetHowToSetupStage from './utils/tour-steps/how-to-setup-stage';
import { TOUR_SELECTOR } from './Tour';

const useHelpTopics = (): Array<{
  title: string;
  onClick: () => void;
  module: 'Contact' | 'Sales' | 'Calendar';
  submodule:
    | 'Leads'
    | 'Companies'
    | 'Deals'
    | 'Tasks'
    | 'Invoice'
    | 'Event'
    | 'Booking';
}> => {
  //Deals
  const showCreateDealSteps = useGetHowToCreateDealSteps();
  const showQuickCreateDealSteps = useGetHowToQuickCreateDealSteps();
  const showViewCreatedDealsSteps = useGetViewCreatedDeals();
  const showManageDealSteps = useGetHowToManageDealSteps();
  const showSetupPipelineSteps = useGetHowToSetupPipelineSteps();
  const showSetupStageSteps = useGetHowToSetupStage();

  //Tasks
  const showViewQuickCreateTaskSteps = useGetHowToQuickCreateTaskSteps();
  const showViewCreateTaskSteps = useGetHowToCreateTaskSteps();
  const showViewManageTaskSteps = useGetHowToManageTaskSteps();

  //Invoices
  const showCreateInvoiceSteps = useGetHowToCreateInvoice();
  const showQuickCreateInvoiceSteps = useGetHowToQuickCreateInvoice();
  const showManageInvoice = useGetHowToManageInvoice();

  //Leads
  const showCreateLeadSteps = useGetHowToCreateLeadSteps();
  const showQuickCreateLeadSteps = useGetHowToQuickCreateLeadSteps();
  const showViewCreatedLeadsSteps = useGetViewCreatedLeads();

  //Companies
  const showCreateCompanySteps = useGetHowToCreateCompanySteps();
  const showQuickCreateCompanySteps = useGetHowToQuickCreateCompanySteps();
  const showViewCreatedCompanySteps = useGetViewCreatedCompanies();

  //Calendar
  const showViewQuickCreateEventSteps = useGetHowToQuickCreateEventSteps();
  const showViewCreateEventSteps = useGetHowToCreateEventSteps();
  const showViewManageEventSteps = useGetHowToManageEventSteps();

  //Booking
  const showViewQuickCreateBookingSteps = useGetHowToQuickCreateBookingSteps();
  const showViewCreateBookingSteps = useGetHowToCreateBookingSteps();
  const showViewManageBookingSteps = useGetHowToManageBookingSteps();

  return [
    {
      title: 'How to quick create a Lead Contact',
      onClick: showQuickCreateLeadSteps,
      module: 'Contact',
      submodule: 'Leads',
    },
    {
      title: 'How to create a Lead Contact',
      onClick: showCreateLeadSteps,
      module: 'Contact',
      submodule: 'Leads',
    },
    {
      title: 'How to view created Lead Contacts',
      onClick: showViewCreatedLeadsSteps,
      module: 'Contact',
      submodule: 'Leads',
    },
    {
      title: 'How to quick create a Company Contact',
      onClick: showQuickCreateCompanySteps,
      module: 'Contact',
      submodule: 'Companies',
    },
    {
      title: 'How to create a Company Contact',
      onClick: showCreateCompanySteps,
      module: 'Contact',
      submodule: 'Companies',
    },
    {
      title: 'How to view created Company Contacts',
      onClick: showViewCreatedCompanySteps,
      module: 'Contact',
      submodule: 'Companies',
    },
    {
      title: 'How to create a Deal from the Deals Page',
      onClick: showCreateDealSteps,
      module: 'Sales',
      submodule: 'Deals',
    },
    {
      title: 'How to quick create a Deal',
      onClick: showQuickCreateDealSteps,
      module: 'Sales',
      submodule: 'Deals',
    },
    {
      title: 'How to view created Deals',
      onClick: showViewCreatedDealsSteps,
      module: 'Sales',
      submodule: 'Deals',
    },
    {
      title: 'How to manage a Deal',
      onClick: showManageDealSteps,
      module: 'Sales',
      submodule: 'Deals',
    },
    {
      title: 'How to setup a Pipeline',
      onClick: showSetupPipelineSteps,
      module: 'Sales',
      submodule: 'Deals',
    },
    {
      title: 'How to setup a Stage',
      onClick: showSetupStageSteps,
      module: 'Sales',
      submodule: 'Deals',
    },
    {
      title: 'How to quick create a Task',
      onClick: showViewQuickCreateTaskSteps,
      module: 'Sales',
      submodule: 'Tasks',
    },
    {
      title: 'How to create a Task',
      onClick: showViewCreateTaskSteps,
      module: 'Sales',
      submodule: 'Tasks',
    },
    {
      title: 'How to manage a Task',
      onClick: showViewManageTaskSteps,
      module: 'Sales',
      submodule: 'Tasks',
    },
    {
      title: 'How to quick create an Invoice',
      onClick: showQuickCreateInvoiceSteps,
      module: 'Sales',
      submodule: 'Invoice',
    },
    {
      title: 'How to create an Invoice',
      onClick: showCreateInvoiceSteps,
      module: 'Sales',
      submodule: 'Invoice',
    },
    {
      title: 'How to manage an Invoice',
      onClick: showManageInvoice,
      module: 'Sales',
      submodule: 'Invoice',
    },
    {
      title: 'How to quick create a Calendar Event',
      onClick: showViewQuickCreateEventSteps,
      module: 'Calendar',
      submodule: 'Event',
    },
    {
      title: 'How to create a Calendar Event',
      onClick: showViewCreateEventSteps,
      module: 'Calendar',
      submodule: 'Event',
    },
    {
      title: 'How to manage a Calendar Event',
      onClick: showViewManageEventSteps,
      module: 'Calendar',
      submodule: 'Event',
    },
    {
      title: 'How to quick create a Calendar Booking',
      onClick: showViewQuickCreateBookingSteps,
      module: 'Calendar',
      submodule: 'Booking',
    },
    {
      title: 'How to create a Calendar Booking',
      onClick: showViewCreateBookingSteps,
      module: 'Calendar',
      submodule: 'Booking',
    },
    {
      title: 'How to manage a Calendar Booking',
      onClick: showViewManageBookingSteps,
      module: 'Calendar',
      submodule: 'Booking',
    },
  ];
};

const Help = () => {
  const { state } = useProvider();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const helpButtonRef = useRef<HTMLButtonElement | null>(null);
  const beginTutorial = useBeginTutorial();

  const onSubmit: React.FormEventHandler<HTMLInputElement> = (event) => {
    setQuery(event.currentTarget.value);
  };

  useEffect(() => {
    if (state.isOpenHelp) {
      // helpButtonRef.current?.click();
      setTimeout(() => {
        beginTutorial();
      }, 400);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isOpenHelp]);

  const helpTopics = useHelpTopics();

  const filteredHelpTopicsByQuery = helpTopics.filter((topic) =>
    topic.title.toLowerCase().includes(query.toLowerCase()),
  );

  const helpTopicGroups = groupBy(filteredHelpTopicsByQuery, 'submodule');
  const helpTopicGroupItems = useCallback(
    (close: () => void) =>
      Object.keys(helpTopicGroups).map((submodule) => {
        const getIcon = (module: string) => {
          switch (module.toUpperCase()) {
            case 'CONTACT':
              return (
                <ProfileIcon
                  key={'CONTACT'}
                  className="w-6 h-6 transition group-hover:text-primary-base"
                />
              );
            case 'CALENDAR':
              return (
                <CalendarIcon
                  key={'CALENDAR'}
                  className="w-6 h-6 transition group-hover:text-primary-base"
                />
              );
            case 'SALES':
              return (
                <SalesIcon
                  key={'SALES'}
                  className="w-6 h-6 transition group-hover:text-primary-base"
                />
              );
            default:
              return (
                <ProfileIcon
                  key={'BASE'}
                  className="w-6 h-6 transition group-hover:text-primary-base"
                />
              );
          }
        };

        //Help List Group
        return (
          <div key={submodule} className={cn('flex flex-col flex-start gap-4')}>
            <div
              className={'uppercase font-semibold'}
            >{`${helpTopicGroups[submodule][0].module} - ${submodule}`}</div>
            {helpTopicGroups[submodule].map((item) => {
              return (
                <button
                  onClick={() => {
                    item.onClick();
                    close();
                  }}
                  key={item.title}
                  className="flex gap-[14px] items-center group transition"
                >
                  <div className="h-11 w-11 bg-fill-primary flex-shrink-0 rounded-full grid place-items-center">
                    {getIcon(item.module.toUpperCase())}
                  </div>

                  <div
                    className={cn(
                      'text-sm mr-1',
                      'text-left',
                      'font-semibold',
                      'transition',
                      'text-secondary',
                      'group-hover:text-primary-base',
                    )}
                  >
                    {item.title}
                  </div>
                </button>
              );
            })}
          </div>
        );
      }),
    [helpTopicGroups],
  );

  return (
    <Popover className={cn('relative', 'flex', 'flex-col', 'items-center')}>
      {({ close, open }) => (
        <>
          <Popover.Button
            ref={helpButtonRef}
            type="button"
            className="inline-flex items-center"
            data-tut={TOUR_SELECTOR.TOUR.HELP_BUTTON}
          >
            <InfoIcon className="w-[20px] h-[20px] text-secondary-dark hover:text-primary-base transition" />
          </Popover.Button>
          <Transition
            as={Popover.Panel}
            className={cn(
              'shadow-leapify-lg',
              'absolute',
              'z-[99]',
              'p-0',
              'bg-fill-tertiary',
              'w-[340px]',
              'top-full',
              'right-0',
              'font-bold',
              'text-sm',
              'leading-[22px]',
              'tracking-[0.04em]',
              'text-secondary-dark',
            )}
            show={open}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
            data-tut={TOUR_SELECTOR.TOUR.MENU}
          >
            <div
              className={cn(
                'bg-fill-primary',
                'pl-4',
                'pr-[18px]',
                'py-[9px]',
                'flex',
                'justify-between',
                'rounded-t-[6px]',
                'items-center',
              )}
            >
              <div className="font-bold text-sm leading-[22px] tracking-[0.04em]">
                How To Help Walkthroughs
              </div>
              <Popover.Button>
                <MdClose className="w-4 h-4 hover:text-danger-base transition" />
              </Popover.Button>
            </div>
            <div className="px-6 pb-6 space-y-6">
              <Searchbox
                ref={(node) => {
                  if (open) {
                    node?.focus();
                  }
                }}
                onChange={onSubmit}
                placeholder="Search Help"
                className="my-5 leading-[20px] font-normal"
              />
              <button
                className="flex items-center gap-[14px] group"
                onClick={() => navigate('/help')}
              >
                <div
                  className={cn(
                    'p-2.5 rounded-full',
                    'flex items-center justify-center',
                    'bg-[#F1F4F8]',
                  )}
                >
                  <IoMdHelpCircle className="text-primary-base h-6 w-6" />
                </div>
                <p className="transition group-hover:text-primary-base text-base">
                  NEED HELP?
                </p>
              </button>
              <div
                className={cn(
                  'flex',
                  'flex-col',
                  'w-full',
                  'gap-4',
                  'overflow-y-auto',
                  'max-h-[360px]',
                  'scrollbar-thin',
                )}
              >
                {helpTopicGroupItems(close)}
              </div>
              {/* <div
              className={cn(
                'border-t-[0.5px]',
                'flex',
                'gap-3',
                'items-end',
                'justify-end',
                'border-t-[#E4EDF0]',
                'pt-[19px]',
              )}
            >
              <Button intent="primary" label="CALL NOW" />
              <Button intent="tertiary" label="CHAT NOW" />
            </div> */}
            </div>
          </Transition>
        </>
      )}
    </Popover>
  );
};

export default Help;
